import { Link, useNavigate } from "@opendash/router";
import { Button, Card, Dropdown, List, Menu } from "antd";
import React from "react";
import styled from "styled-components";

import { useTranslation } from "@opendash/core";
import { Icon } from "@opendash/icons";
import { createComponent } from "@opendash/state";

import { Article, Category, Document, Video } from "../types-generated";

import type { AppState } from "./App.state";

import { $parse, Parse } from "@opendash/plugin-parse";
import { KnowledgeArtikelView } from "./KnowledgeArtikelView";
import { KnowledgeDocumentView } from "./KnowledgeDocumentView";
import { KnowledgeVideoView } from "./KnowledgeVideoView";
import { ActionBar } from "./styles";

const CoverIcon = styled.div`
  background: #eaedf2;
  text-align: center;
  font-size: 50px;
  padding: 40px 0;
`;

export const KnowledgeContent = createComponent(function KnowledgeContent({
  state,
}: {
  state: AppState;
}) {
  const t = useTranslation();
  const navigate = useNavigate();

  if (state.article) {
    return <KnowledgeArtikelView state={state} />;
  }

  if (state.video) {
    return <KnowledgeVideoView state={state} />;
  }

  if (state.document) {
    return <KnowledgeDocumentView state={state} />;
  }

  state.useForceRender();

  return (
    <>
      {state.language &&
        $parse.ui.getClassPermission(Category.className, "create") && (
          <ActionBar>
            {state.category &&
              $parse.ui.getObjectPermission(state.category, "update") && (
                <Button
                  children={t("knowledge:label.update_category")}
                  onClick={() => {
                    state.updateContent(state.category);
                  }}
                />
              )}
            <Dropdown
              children={<Button children={t("knowledge:label.new_content")} />}
              overlay={
                <Menu
                  items={
                    state.category
                      ? [
                          {
                            label: t("knowledge:type.category"),
                            key: "1",
                            icon: <Icon icon="fa:folder" />,
                            onClick: () => {
                              $parse.ui.createObject(
                                Category.className,
                                new Category({
                                  language: new Parse.Object("OD3_Language", {
                                    id: state.language,
                                  }),
                                  parent: state.category,
                                })
                              );
                            },
                          },
                          {
                            label: t("knowledge:type.article"),
                            key: "2",
                            icon: <Icon icon="fa:file-alt" />,
                            onClick: () => {
                              $parse.ui.createObject(
                                Article.className,
                                new Article({
                                  language: new Parse.Object("OD3_Language", {
                                    id: state.language,
                                  }),
                                  category: state.category,
                                })
                              );
                            },
                          },
                          {
                            label: t("knowledge:type.video"),
                            key: "3",
                            icon: <Icon icon="fa:file-video" />,
                            onClick: () => {
                              $parse.ui.createObject(
                                Video.className,
                                new Video({
                                  language: new Parse.Object("OD3_Language", {
                                    id: state.language,
                                  }),
                                  category: state.category,
                                })
                              );
                            },
                          },
                          {
                            label: t("knowledge:type.document"),
                            key: "4",
                            icon: <Icon icon="fa:file-pdf" />,
                            onClick: () => {
                              $parse.ui.createObject(
                                Document.className,
                                new Document({
                                  language: new Parse.Object("OD3_Language", {
                                    id: state.language,
                                  }),
                                  category: state.category,
                                })
                              );
                            },
                          },
                        ]
                      : [
                          {
                            label: t("knowledge:type.category"),
                            key: "1",
                            icon: <Icon icon="fa:folder" />,
                            onClick: () => {
                              $parse.ui.createObject(
                                Category.className,
                                new Category({
                                  language: new Parse.Object("OD3_Language", {
                                    id: state.language,
                                  }),
                                })
                              );
                            },
                          },
                        ]
                  }
                />
              }
            />
          </ActionBar>
        )}

      <List
        dataSource={state.content}
        rowKey={(row) => (typeof row === "string" ? row : row.id)}
        grid={{ gutter: 16, column: 4 }}
        renderItem={(row) => {
          if (typeof row === "string") {
            const language = row;

            return (
              <Link to={`/knowledgebase/${language}`}>
                <List.Item>
                  <Card
                    hoverable
                    style={{ width: 240 }}
                    cover={
                      <CoverIcon>
                        <Icon icon="fa:language" />
                      </CoverIcon>
                    }
                  >
                    <Card.Meta
                      title={language}
                      description={t("knowledge:type.language")}
                    />
                  </Card>
                </List.Item>
              </Link>
            );
          }

          if (row instanceof Category) {
            const category = row;

            return (
              <Link to={`/knowledgebase/${state.language}/${category.id}`}>
                <List.Item>
                  <Card
                    hoverable
                    style={{ width: 240 }}
                    cover={
                      <CoverIcon>
                        <Icon icon="fa:folder" />
                      </CoverIcon>
                    }
                  >
                    <Card.Meta
                      title={category.label}
                      description={t("knowledge:type.category")}
                    />
                  </Card>
                </List.Item>
              </Link>
            );
          }

          if (row instanceof Article) {
            const article = row;

            return (
              <Link
                to={`/knowledgebase/${state.language}/${state.category.id}/article/${article.id}`}
              >
                <List.Item>
                  <Card
                    hoverable
                    style={{ width: 240 }}
                    cover={
                      <CoverIcon>
                        <Icon icon="fa:file-alt" />
                      </CoverIcon>
                    }
                  >
                    <Card.Meta
                      title={article.label}
                      description={t("knowledge:type.article")}
                    />
                  </Card>
                </List.Item>
              </Link>
            );
          }

          if (row instanceof Video) {
            const video = row;

            return (
              <Link
                to={`/knowledgebase/${state.language}/${state.category.id}/video/${video.id}`}
              >
                <List.Item>
                  <Card
                    hoverable
                    style={{ width: 240 }}
                    cover={
                      <CoverIcon>
                        <Icon icon="fa:file-video" />
                      </CoverIcon>
                    }
                  >
                    <Card.Meta
                      title={video.label}
                      description={t("knowledge:type.video")}
                    />
                  </Card>
                </List.Item>
              </Link>
            );
          }

          if (row instanceof Document) {
            const document = row;

            return (
              <Link
                to={`/knowledgebase/${state.language}/${state.category.id}/document/${document.id}/1`}
              >
                <List.Item>
                  <Card
                    hoverable
                    style={{ width: 240 }}
                    cover={
                      <CoverIcon>
                        <Icon icon="fa:file-pdf" />
                      </CoverIcon>
                    }
                  >
                    <Card.Meta
                      title={document.label}
                      description={t("knowledge:type.document")}
                    />
                  </Card>
                </List.Item>
              </Link>
            );
          }

          return null;
        }}
      />
    </>
  );
});
